import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  adminListUsers,
  adminGetKpis,
  adminWeeklyTrend,
  adminDeactivateUser,
  adminReactivateUser,
  adminSetFreeAccess,
  adminDeleteUser,
  adminDeletionLog,
  adminDeletionLogCsv,
  downloadBlob,
  filenameFromResponse,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/format";
import { accountLabel } from "@/lib/accountLabel";
import {
  Users,
  UserCheck,
  UserPlus,
  FileText,
  CalendarClock,
  Search,
  Loader2,
  ShieldCheck,
  Euro,
  Gift,
} from "lucide-react";
import { toast } from "sonner";
import { WeeklyTrendChart } from "@/components/WeeklyTrendChart";
import AdminUserTree from "@/components/AdminUserTree";
import DeletionLog from "@/components/admin/DeletionLog";
import FeaturesPanel from "@/components/admin/FeaturesPanel";
import DeactivateUserDialog from "@/components/admin/DeactivateUserDialog";
import DeleteUserDialog from "@/components/admin/DeleteUserDialog";
import FreeAccessDialog from "@/components/admin/FreeAccessDialog";

// Filter-Pillen: PLAN (STB, UNT, EIN) + ROLLE (MAN, MAA, MAB) gemischt – Volltext per Tooltip.
// Reihenfolge: STB, MAN, UNT, EIN, MAA, MAB.
const ROLE_FILTERS = [
  { key: "STB", label: "Steuerberater", match: (u) => u.plan === "STB" },
  { key: "MAN", label: "Mandant", match: (u) => u.rolle === "MAN" },
  { key: "UNT", label: "Unternehmer", match: (u) => u.plan === "UNT" },
  { key: "EIN", label: "Einzelnutzer", match: (u) => u.plan === "EIN" },
  { key: "MAA", label: "Mitarbeiter mit Admin-Rechten", match: (u) => u.rolle === "MAA" },
  { key: "MAB", label: "Mitarbeiter", match: (u) => u.rolle === "MAB" },
];

// Anzeige-Label eines Accounts (einheitlich: Mitarbeiter = Name, Firma = "Firma | Eigentümer").
const acctLabel = (u) => accountLabel(u) || u?.email || "";

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-border p-4 flex items-center gap-3" data-testid={testId}>
      <div className="h-10 w-10 bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold tabular leading-tight">{value}</div>
        <div className="data-label">{label}</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [users, setUsers] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState([]);
  const [period, setPeriod] = useState("weeks");
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "deactivated" | "trial"
  const [busyId, setBusyId] = useState(null);

  const [target, setTarget] = useState(null);
  const [stage, setStage] = useState(1);
  const [emailConfirm, setEmailConfirm] = useState("");

  const [delTarget, setDelTarget] = useState(null);
  const [delStage, setDelStage] = useState(1);
  const [delEmail, setDelEmail] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const [freeTarget, setFreeTarget] = useState(null);

  const [logEntries, setLogEntries] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, k, log] = await Promise.all([
        adminListUsers(),
        adminGetKpis(),
        adminDeletionLog(),
      ]);
      setUsers(u);
      setKpis(k);
      setLogEntries(log || []);
    } catch {
      toast.error("Admindaten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, [load]);

  useEffect(() => {
    let active = true;
    adminWeeklyTrend(period)
      .then((t) => {
        if (active) setTrend(t?.points || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [period]);

  const matchesQuery = useCallback(
    (u) => {
      if (statusFilter === "deactivated" && u.active) return false;
      if (statusFilter === "trial" && !(u.is_org_payer && u.billing_state === "trial")) return false;
      if (roleFilter) {
        const rf = ROLE_FILTERS.find((f) => f.key === roleFilter);
        if (rf && !rf.match(u)) return false;
      }
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return [u.company, u.name, u.email, u.role_label, u.plan, u.rolle].some((v) =>
        (v || "").toLowerCase().includes(q)
      );
    },
    [query, roleFilter, statusFilter]
  );

  const inactiveCount = useMemo(() => users.filter((u) => !u.active).length, [users]);
  const trialCount = useMemo(
    () => users.filter((u) => u.is_org_payer && u.billing_state === "trial").length,
    [users]
  );

  // Filter-Pillen-Zählung nach kombinierten PLAN/ROLLE-Filtern.
  const roleCounts = useMemo(() => {
    const c = {};
    for (const u of users)
      for (const f of ROLE_FILTERS) if (f.match(u)) c[f.key] = (c[f.key] || 0) + 1;
    return c;
  }, [users]);

  const impersonate = async (u) => {
    setBusyId(u.id);
    try {
      await startImpersonation(u.id);
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Account-Einblick fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  const reactivate = async (u) => {
    setBusyId(u.id);
    try {
      await adminReactivateUser(u.id);
      toast.success(`${acctLabel(u)} wurde reaktiviert.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reaktivierung fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  const openDeactivate = (u) => {
    setTarget(u);
    setStage(1);
    setEmailConfirm("");
  };
  const closeDeactivate = () => {
    setTarget(null);
    setStage(1);
    setEmailConfirm("");
  };
  const confirmDeactivate = async () => {
    if (!target) return;
    setBusyId(target.id);
    try {
      await adminDeactivateUser(target.id);
      toast.success(`${acctLabel(target)} wurde deaktiviert.`);
      closeDeactivate();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deaktivierung fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  const emailMatches =
    target && emailConfirm.trim().toLowerCase() === (target.email || "").toLowerCase();

  const openFree = (u) => setFreeTarget(u);
  const closeFree = () => setFreeTarget(null);
  const confirmFree = async () => {
    if (!freeTarget) return;
    const enable = !freeTarget.lifetime_free;
    setBusyId(freeTarget.id);
    try {
      await adminSetFreeAccess(freeTarget.id, enable);
      toast.success(
        enable
          ? `${acctLabel(freeTarget)} ist jetzt dauerhaft kostenlos.`
          : `Kostenloser Zugang für ${acctLabel(freeTarget)} wurde entzogen.`
      );
      setFreeTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aktion fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  const openDelete = (u) => {
    setDelTarget(u);
    setDelStage(1);
    setDelEmail("");
  };
  const closeDelete = () => {
    setDelTarget(null);
    setDelStage(1);
    setDelEmail("");
  };
  const delEmailMatches =
    delTarget && delEmail.trim().toLowerCase() === (delTarget.email || "").toLowerCase();
  const confirmDelete = async () => {
    if (!delTarget) return;
    setDelBusy(true);
    try {
      // Backend builds the export, stores it for 7 days, emails the link to
      // office@besca.eu, then permanently deletes the account & all data.
      const res = await adminDeleteUser(delTarget.id);
      // Trigger the immediate download via the same token link (no blob/CORS issues).
      if (res?.download_url) {
        const a = document.createElement("a");
        a.href = res.download_url;
        a.download = res.filename || "Besca_Export.zip";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => a.remove(), 2000);
      }
      toast.success(
        `${acctLabel(delTarget)} wurde endgültig gelöscht. Der Export wurde heruntergeladen und der 7-Tage-Download-Link an office@besca.eu gesendet.`
      );
      closeDelete();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen.");
    } finally {
      setDelBusy(false);
    }
  };

  const filteredLog = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return logEntries;
    return logEntries.filter((e) =>
      [e.company, e.name, e.email].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [logEntries, query]);

  const groupedLog = useMemo(() => {
    const groups = {};
    for (const e of filteredLog) {
      const d = e.date || (e.deleted_at || "").slice(0, 10);
      (groups[d] = groups[d] || []).push(e);
    }
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredLog]);

  const downloadLogCsv = async (date) => {
    try {
      const params = {};
      if (date) params.date = date;
      if (query.trim()) params.q = query.trim();
      const res = await adminDeletionLogCsv(params);
      downloadBlob(
        res.data,
        filenameFromResponse(res, `Besca_Loeschprotokoll_${date || "gesamt"}.csv`)
      );
    } catch {
      toast.error("Protokoll-CSV konnte nicht erstellt werden.");
    }
  };

  const fmtDay = (d) => {
    const dt = new Date(`${d}T00:00:00`);
    return Number.isNaN(dt.getTime())
      ? d
      : dt.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };
  const fmtTime = (iso) => {
    const dt = new Date(iso);
    return Number.isNaN(dt.getTime())
      ? ""
      : dt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const periodUnit = period === "days" ? "Tag" : period === "months" ? "Monat" : "Woche";

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold">Administration</h1>
          <p className="text-muted-foreground text-sm">Nutzerverwaltung und Kennzahlen.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Kpi icon={Users} label="Nutzer gesamt" value={kpis?.total_users ?? 0} testId="kpi-total-users" />
        <Kpi icon={UserCheck} label="Aktive Nutzer" value={kpis?.active_users ?? 0} testId="kpi-active-users" />
        <Kpi icon={UserPlus} label="Neu (7 Tage)" value={kpis?.new_users_7d ?? 0} testId="kpi-new-users" />
        <Kpi icon={FileText} label="Belege gesamt" value={kpis?.total_receipts ?? 0} testId="kpi-total-receipts" />
        <Kpi icon={CalendarClock} label="Scans (7 Tage)" value={kpis?.receipts_7d ?? 0} testId="kpi-receipts-7d" />
        <Kpi
          icon={Euro}
          label="Belegwert gesamt"
          value={formatMoney(kpis?.total_receipt_value ?? 0, "EUR")}
          testId="kpi-receipt-value"
        />
        <Kpi
          icon={Gift}
          label="LifeTime vergeben"
          value={`${kpis?.lifetime_used ?? 0}/${kpis?.lifetime_limit ?? 100}`}
          testId="kpi-lifetime"
        />
      </div>

      <FeaturesPanel />

      <div className="space-y-3" data-testid="admin-trends">
        <div className="flex items-center justify-end">
          <div
            className="inline-flex border border-border rounded-md overflow-hidden"
            data-testid="trend-period-toggle"
          >
            {[
              { key: "days", label: "365 Tage" },
              { key: "weeks", label: "52 Wochen" },
              { key: "months", label: "12 Monate" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setPeriod(opt.key)}
                data-testid={`trend-period-${opt.key}`}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  period === opt.key
                    ? "bg-primary text-white"
                    : "bg-white text-muted-foreground hover:bg-primary/10"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <WeeklyTrendChart
            points={trend}
            dataKey="scans"
            title="Beleg Verlauf"
            valueLabel="Belege"
            unit={periodUnit}
            color="#00b29c"
            testId="weekly-trend-chart"
          />
          <WeeklyTrendChart
            points={trend}
            dataKey="revenue"
            title="Umsatzentwicklung"
            valueLabel="Umsatz"
            isMoney
            unit={periodUnit}
            color="#002d26"
            testId="revenue-trend-chart"
          />
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Suche: Firma, Name, E-Mail (inkl. Lösch-Protokoll)"
          className="pl-9 bg-white rounded-none"
          data-testid="admin-search"
        />
      </div>

      <div className="space-y-2" data-testid="role-filter">
        {/* Status-Pillen (sehr helles Grau) */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setRoleFilter("");
              setStatusFilter("");
            }}
            data-testid="role-filter-all"
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              roleFilter === "" && statusFilter === ""
                ? "bg-slate-200 text-slate-700 border-slate-300"
                : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
            }`}
          >
            Alle ({users.length})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter((s) => (s === "deactivated" ? "" : "deactivated"))}
            data-testid="role-filter-deactivated"
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              statusFilter === "deactivated"
                ? "bg-slate-200 text-slate-700 border-slate-300"
                : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
            }`}
          >
            Deaktiviert ({inactiveCount})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter((s) => (s === "trial" ? "" : "trial"))}
            data-testid="role-filter-trial"
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              statusFilter === "trial"
                ? "bg-slate-200 text-slate-700 border-slate-300"
                : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
            }`}
          >
            Testphase ({trialCount})
          </button>
        </div>
        {/* Rollen-Pillen (Hover behält Primärfarbe) */}
        <div className="flex flex-wrap items-center gap-2">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setRoleFilter((cur) => (cur === f.key ? "" : f.key))}
              data-testid={`role-filter-${f.key}`}
              title={f.label}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                roleFilter === f.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-white text-muted-foreground border-border hover:bg-primary/10 hover:text-primary hover:border-primary/30"
              }`}
            >
              {f.key} ({roleCounts[f.key] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* Hierarchische Nutzerübersicht (Baum) */}
      <AdminUserTree
        users={users}
        matchesQuery={matchesQuery}
        filterActive={!!(query.trim() || roleFilter || statusFilter)}
        busyId={busyId}
        onImpersonate={impersonate}
        onDeactivate={openDeactivate}
        onReactivate={reactivate}
        onDelete={openDelete}
        onToggleFree={openFree}
      />

      {/* Deletion / export protocol */}
      <DeletionLog
        showLog={showLog}
        setShowLog={setShowLog}
        filteredLog={filteredLog}
        groupedLog={groupedLog}
        query={query}
        downloadLogCsv={downloadLogCsv}
        fmtDay={fmtDay}
        fmtTime={fmtTime}
      />

      <DeactivateUserDialog
        target={target}
        stage={stage}
        setStage={setStage}
        emailConfirm={emailConfirm}
        setEmailConfirm={setEmailConfirm}
        emailMatches={emailMatches}
        busyId={busyId}
        closeDeactivate={closeDeactivate}
        confirmDeactivate={confirmDeactivate}
      />

      <DeleteUserDialog
        delTarget={delTarget}
        delStage={delStage}
        setDelStage={setDelStage}
        delEmail={delEmail}
        setDelEmail={setDelEmail}
        delEmailMatches={delEmailMatches}
        delBusy={delBusy}
        closeDelete={closeDelete}
        confirmDelete={confirmDelete}
      />

      <FreeAccessDialog
        target={freeTarget}
        busyId={busyId}
        onClose={closeFree}
        onConfirm={confirmFree}
      />
    </div>
  );
}
